import { CircleInfoIcon, PenIcon } from "@xola/icons";
import { Label, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { SwitchField } from "common";
import React from "react";
import { SelectedExperiences, SelectionState } from "types";
import { SelectedMemberships } from "types/src/types/SelectedMemberships";
import { SelectedMerchandise } from "types/src/types/SelectedMerchandise";
import { getSelectionState } from "../../models/SelectionState";
import { SecondaryButton } from "./SecondaryButton";
import { SelectedProductList } from "./SelectedProductList";

interface SelectedProductListInputProps {
    label?: string;
    selectedProducts: SelectedExperiences | SelectionState | SelectedMemberships | SelectedMerchandise;
    isSelectAll?: boolean;
    labelTooltipContent?: string;
    onChange?: (value: SelectionState) => void;
    onEditClick: () => void;
    error?: string | string[] | null;
    isDisabled?: boolean;
    showDragAndDropProductList?: boolean;
    showNoProductCard?: boolean;
}

export const SelectedProductListInput = ({
    label = "Products",
    selectedProducts,
    isSelectAll,
    labelTooltipContent,
    onChange,
    onEditClick,
    error,
    isDisabled = false,
    showDragAndDropProductList = false,
    showNoProductCard = false,
}: SelectedProductListInputProps) => {
    const selectionState =
        "experiences" in selectedProducts || "memberships" in selectedProducts || "merchandise" in selectedProducts
            ? getSelectionState(selectedProducts)
            : selectedProducts;
    const isProductSelectorDisabled = isDisabled || (isSelectAll ? selectedProducts.all : false);

    return (
        <div>
            <div
                className="mb-2 flex items-end justify-between"
                data-testid={`selected-product-list-input-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
                <div className="flex items-center space-x-1">
                    <Label className={clsx("whitespace-nowrap", error && "!text-danger")}>
                        {label} {error}
                    </Label>
                    {labelTooltipContent && (
                        <Tooltip placement="auto" content={labelTooltipContent}>
                            <CircleInfoIcon />
                        </Tooltip>
                    )}
                </div>

                <div>
                    {isSelectAll ? (
                        <SwitchField
                            size="small"
                            isChecked={selectedProducts.all}
                            isDisabled={isDisabled}
                            onChange={(isChecked) => onChange?.({ all: isChecked, selected: [] })}
                        >
                            Select All
                        </SwitchField>
                    ) : null}

                    <SecondaryButton
                        className="ml-4"
                        size="small"
                        disabled={isProductSelectorDisabled}
                        onClick={onEditClick}
                    >
                        <PenIcon
                            data-testid={`selected-product-list-edit-${label.toLowerCase().replace(/\s+/g, "-")}`}
                        />
                    </SecondaryButton>
                </div>
            </div>

            {showNoProductCard && selectionState.selected.length === 0 ? (
                <NoProductsCard isDisabled={isProductSelectorDisabled} onEditClick={onEditClick} />
            ) : isSelectAll && selectionState.selected.length === 0 ? null : (
                <SelectedProductList
                    selectedProducts={selectionState}
                    isError={!!error}
                    showDragAndDropProductList={showDragAndDropProductList}
                    onChange={onChange}
                />
            )}
        </div>
    );
};

export const NoProductsCard = ({ isDisabled, onEditClick }: { isDisabled?: boolean; onEditClick: () => void }) => {
    return (
        <div className="border border-gray-lighter p-6 text-center">
            <h3 className="">No products</h3>
            <p className="mb-4 text-sm text-gray-darker">Please select at least 2 products</p>
            <SecondaryButton size="small" disabled={isDisabled} onClick={onEditClick}>
                Select product
            </SecondaryButton>
        </div>
    );
};
